import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridColDef, GridPaginationModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TachographDownloadRow } from "../models/Tachograph";
import { formatUtcDateOrEmpty } from "../utilities/tachographDates";
import { TACHOGRAPH_PAGE_SIZES } from "./tachographGridDefaults";
import { I18nGrid } from "@/components/data/I18nGrid";
import { ExportToolbar } from "@/components/grid/ExportToolbar";
import { LoadFailedOverlay } from "@/components/grid/LoadFailedOverlay";
import { NoRowsOverlay } from "@/components/grid/NoRowsOverlay";

type Props = {
  rows: TachographDownloadRow[];
  loading: boolean;
  /** True while a source of the rows is loading or failed. */
  exportDisabled: boolean;
  /** True when a source of the rows failed: an empty grid then says so instead of "no rows". */
  loadFailed: boolean;
  isDownloading: boolean;
  onDownload: (fileIds: string[]) => void;
};

const DEFAULT_PAGE_SIZE = 100;

/** The readout files, with the selected ones saved to the browser's download folder. */
export function TachographDownloadsPanel({
  rows,
  loading,
  exportDisabled,
  loadFailed,
  isDownloading,
  onDownload,
}: Props) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const columns = useMemo<GridColDef<TachographDownloadRow>[]>(
    () => [
      { field: "source", headerName: t("tachograph.columns.source"), width: 90 },
      { field: "fileName", headerName: t("tachograph.columns.fileName"), minWidth: 360, flex: 1.3 },
      { field: "unit", headerName: t("tachograph.columns.unit"), minWidth: 260, flex: 1 },
      {
        field: "dataFromUtc",
        headerName: t("tachograph.columns.dataFromUtc"),
        width: 140,
        type: "date",
        valueGetter: (_value, row) => row.dataFromUtc,
        valueFormatter: formatUtcDateOrEmpty,
      },
      {
        field: "dataToUtc",
        headerName: t("tachograph.columns.dataToUtc"),
        width: 140,
        type: "date",
        valueGetter: (_value, row) => row.dataToUtc,
        valueFormatter: formatUtcDateOrEmpty,
      },
      {
        field: "createdAt",
        headerName: t("tachograph.columns.createdAt"),
        width: 140,
        type: "date",
        valueGetter: (_value, row) => row.createdAt,
        valueFormatter: formatUtcDateOrEmpty,
      },
      {
        field: "addedAt",
        headerName: t("tachograph.columns.addedAt"),
        width: 140,
        type: "date",
        valueGetter: (_value, row) => row.addedAt,
        valueFormatter: formatUtcDateOrEmpty,
      },
    ],
    [t],
  );

  const pageCount = paginationModel.pageSize
    ? Math.ceil(rows.length / paginationModel.pageSize)
    : 0;

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("tachograph.pageCount", { count: pageCount })}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={selectedIds.length === 0 || isDownloading}
          onClick={() => void onDownload(selectedIds.map(String))}
        >
          {t("tachograph.downloadSelected", { count: selectedIds.length })}
        </Button>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <I18nGrid
          rows={rows}
          columns={columns}
          loading={loading || isDownloading}
          pagination
          disableRowSelectionOnClick
          checkboxSelection
          keepNonExistentRowsSelected={false}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          pageSizeOptions={TACHOGRAPH_PAGE_SIZES}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          slots={{
            noRowsOverlay: loadFailed ? LoadFailedOverlay : NoRowsOverlay,
            toolbar: ExportToolbar,
          }}
          slotProps={{
            toolbar: { report: { title: t("tachograph.tabs.downloads") }, exportDisabled },
          }}
          settingsKey="tachographDownloads"
          initialState={{ density: "compact", sorting: { sortModel: [{ field: "addedAt", sort: "desc" }] } }}
        />
      </Box>
    </Stack>
  );
}
